/* eslint-disable */
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import VariantTable from '../Food Products/VariantPrice/VariantTable';
import analyticsServices from '@/Services/analytics.services';
import { useIssuanceAnalysisStore } from './IssuanceStore';
import { deserializer, formatDate, MONTH_NAMES_SHORT, quickPickerRanges } from './issuanceAnalysisHelper';
import { CheckPicker, DateRangePicker } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { filtersInfoChip, handleExportData, useQuery } from '@/Helpers';
import { notificationServices } from '@/Services';
import { Filter, Upload } from 'lucide-react';
import { Button } from '@/Components/ui/button';
import drawerActions from '@/Actions/drawer.actions';
import ImagesFinder from '@/Components/ImagesFinder/ImagesFinder';
import GlobalSearch from '@/Components/globalSearch/search';

const tabList = [
	{ label: 'PRODUCTION', value: 'production' },
	{ label: 'ISSUANCE', value: 'issuance' },
];

const DAY_SUFFIXES = ['th', 'st', 'nd', 'rd'];

function getDaySuffix(day) {
	if (!day) return '';
	const j = day % 10;
	const k = day % 100;
	if (k >= 11 && k <= 13) return `${day}th`;
	return `${day}${DAY_SUFFIXES[j] || 'th'}`;
}

const filtersInfo = {
	header: {
		store: false,
		date: false,
	},
	kitchenBaseStore: {
		show: true,
		text: 'Store',
		multiSelect: true,
	},
	dateRangeOnly: {
		show: true,
		text: 'Date Range',
	}
}
const exportInfo = {
	showExportBtn: true,
};

function IssuanceListPage() {
	const state = useIssuanceAnalysisStore.getState();
	const query = useQuery();
	const hideFilters = query.get('hide');
	const dispatch = useDispatch();

	const [tabValue, setTabValue] = useState('issuance');
	const [comingColumns, setComingColumns] = useState([]);
	const [data, setData] = useState([]);
	const [searchValue, setSearchValue] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [filters, setFilters] = useState({});

	const processedColumns = useMemo(() => {
		if (!comingColumns?.length) return [];
		return comingColumns.map((column) => ({
			...column,
			title: column?.label,
			searchable: column?.key === 'productName' ? true : column?.searchable,
		}));
	}, [comingColumns]);

	const fetchProductionList = useCallback(async (options = {}) => {
		const {
			tabType = tabValue,
			filters = {},
		} = options;

		setIsLoading(true);
		const processedReq = deserializer(state, filters, hideFilters);
		try {
			const req = {
				data: {
					...processedReq,
					selectedTab: tabType,
				},
			};

			const response = await analyticsServices.fetchProductionList(req);

			if (response.statusCode === 200) {
				const { data, columns } = response?.data;
				setComingColumns(columns || []);
				setData(data || []);
			} else {
				setComingColumns([]);
				setData([]);
			}
		} catch (error) {
			console.error('Error fetching production list:', error);
			setComingColumns([]);
			setData([]);
			notificationServices.generateNotification({
				type: 'error',
				message: error?.message ?? 'Failed to fetch production list',
			})
		} finally {
			setIsLoading(false);
		}
	}, [tabValue, state, hideFilters, filters]);

	useEffect(() => {
		if (hideFilters) fetchProductionList();
	}, []);

	const handleTabChange = useCallback((value) => {
		setTabValue(value);
		fetchProductionList({
			tabType: value,
			...(!hideFilters ? {
				filters: {
					...filters,
					storeId: filters?.fromPodFilter?.id ? [filters?.fromPodFilter?.id] : [],
					dateRange: {
						from: filters?.dateRange?.from ? moment(filters?.dateRange.from).format('YYYY-MM-DD') : moment().startOf('month').format('YYYY-MM-DD'),
						to: filters?.dateRange?.to ? moment(filters?.dateRange.to).format('YYYY-MM-DD') : moment(new Date()).format('YYYY-MM-DD'),
					}
				}
			} : {})
		});
	}, [fetchProductionList, filters]);

	const customRowData = useMemo(() => {
		const base = {};
		processedColumns.forEach((column) => {
			base[column.key] = (product) => (
				<div className="text-gray-700 text-left text-[12px]">
					{product?.[column.key] ?? ''}
				</div>
			);
		});
		return base;
	}, [processedColumns]);

	const sortInfo = useMemo(() => {
		const info = {};
		processedColumns.forEach((column) => {
			info[column.key] = {
				type: column?.dataType,
				path: `obj?.${column.key}`,
			};
		});
		return info;
	}, [processedColumns]);

	const handleFiltersSubmit = useCallback((filtersFrom) => {
		const transformedFilters = {
			storeId: filtersFrom?.fromPodFilter?.id ? [filtersFrom?.fromPodFilter?.id] : [],
			dateRange: {
				from: filtersFrom?.dateRange?.from ? moment(filtersFrom.dateRange.from).format('YYYY-MM-DD') : moment().startOf('month').format('YYYY-MM-DD'),
				to: filtersFrom?.dateRange?.to ? moment(filtersFrom.dateRange.to).format('YYYY-MM-DD') : moment(new Date()).format('YYYY-MM-DD'),
			}
		};
		setFilters(filtersFrom);
		fetchProductionList({ filters: transformedFilters });
	}, [fetchProductionList, filters]);

	const renderDateInfo = useCallback(() => {
		const { year, month, day } = state?.dateFilter || {};
		if (!year && month === undefined && !day) return null;

		return (
			<div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-md border border-gray-200 shadow-sm">
				<span className="text-slate-500 font-semibold text-xs tracking-wide uppercase">
					{year && <>Year <span className="text-slate-700">{year}</span></>}
					{typeof month === 'number' && month >= 0 && (
						<>
							{year && ','} Month{' '}
							<span className="text-slate-700">{MONTH_NAMES_SHORT?.[Number(month) - 1]}</span>
						</>
					)}
					{day && (
						<>
							{(year || (typeof month === 'number' && month >= 0)) && ','} Day{' '}
							<span className="text-slate-700">{getDaySuffix(day)}</span>
						</>
					)}
				</span>
			</div>
		);
	}, [state?.dateFilter]);

	const dropDownItems = useMemo(() => {
		if (!processedColumns.length) return [];
		return processedColumns
			.filter((column) => column?.searchable)
			.map((column) => ({
				label: column?.label,
				value: column?.key,
				placeholder: `Search by ${column?.label}`,
			}));
	}, [processedColumns]);


	const filterData = useMemo(() => {
		if (!data.length) return [];
		if (!searchValue?.searchValue || !searchValue?.value) return data;

		const searchLower = String(searchValue.searchValue).toLowerCase();
		return data.filter((item) => {
			const itemValue = String(item?.[searchValue.value] ?? '').toLowerCase();
			return itemValue.includes(searchLower);
		});
	}, [data, searchValue?.searchValue, searchValue?.value]);

	const handleExcelDownload = useCallback((exportFilters) => {
		const headers = processedColumns.map((column) => column?.label);
		const rows = data.map((item) => {
			return processedColumns.map((header) => {
				return item?.[header?.key] ?? '';
			});
		});
		const csvData = [headers, ...rows];
		const csvContent = csvData.map((row) => row.join(',')).join('\n');
		const csvFile = new Blob([csvContent], { type: 'text/csv' });
		const downloadLink = document.createElement('a');
		downloadLink.download = `production-analysis-list-${moment().format('YYYY-MM-DD')}.csv`;
		downloadLink.href = window.URL.createObjectURL(csvFile);
		downloadLink.style.display = 'none';
		document.body.appendChild(downloadLink);
		downloadLink.click();
		window.URL.revokeObjectURL(downloadLink.href);
		document.body.removeChild(downloadLink);
	}, [data, processedColumns]);

	return (
		<div className="bg-gray-50 flex flex-col gap-3 px-4 py-6">
			<ImagesFinder
				filtersInfo={filtersInfo}
				exportInfo={exportInfo}
				onSubmit={handleFiltersSubmit}
				avoidInitialCall={true}
				filterLabels={{ fromPod: 'Select Store' }}
				cleanable={{ fromPod: true }}
				initialState={{
					dateRange: {
						active: true,
						from: new Date(moment().startOf('month')),
						to: new Date(),
					}
				}}
			/>
			<div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
				<span className="block text-[20px] font-extrabold text-slate-800 tracking-tight leading-snug">
					Issuance Analysis List
				</span>
				{hideFilters && renderDateInfo()}
			</div>

			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-y-2 gap-x-3 px-2 w-full max-w-full bg-white pt-2 pb-2">
				<div className="flex items-center bg-gray-100 rounded-md border border-gray-200 shadow-sm w-fit overflow-x-auto">
					{tabList.map((ele) => {
						const isActive = tabValue === ele.value;
						return (
							<button
								key={ele.value}
								type="button"
								className={`text-center !text-[14px] px-4 py-2 text-base font-medium cursor-pointer min-w-max transition-colors
									${isActive ? 'bg-yellow-200' : 'hover:bg-gray-50'} 
									${isActive ? 'text-gray-900' : 'text-gray-700'}`}
								onClick={() => handleTabChange(ele.value)}
								disabled={isLoading}
								style={{
									borderRight: ele.value !== tabList[tabList.length - 1].value ? '1px solid #ececec' : 'none',
								}}
							>
								{ele.label}
							</button>
						);
					})}
				</div>

				<div className="flex flex-1 min-w-0 max-w-full items-center ml-0 md:ml-3">
					<GlobalSearch
						placeholderText="Search by Production Analysis"
						setSelectedValue={setSearchValue}
						dropDownOptions={dropDownItems}
						searchDropDown={dropDownItems?.length > 0}
					/>
				</div>

				{!hideFilters && (
					<div className="flex flex-row items-baseline gap-2 flex-shrink-0 ml-0 md:ml-3">
						{filtersInfoChip(filtersInfo, filters)}
						<Button
							onClick={() => {
								dispatch(drawerActions.toggleFiltersDrawer(true, {
									handleDataExport: (filtersState) =>
										handleExcelDownload(filtersState),
								}));
							}}
							className="flex items-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700 text-nowrap"
						>
							<span className="text-nowrap">Filter & Export</span>
							<Filter className="size-4" />
						</Button>
					</div>
				)}
				{hideFilters && (
					<Button
						className='flex justify-center gap-2 px-4 py-2 text-white bg-teal-600 border border-gray-300 rounded-md hover:bg-teal-700'
						onClick={() => handleExcelDownload(data)}
						disabled={!data?.length}
					>
						<Upload size={16} />
						<span className='font-bold normal-case'>Export (.csv)</span>
					</Button>
				)}
			</div>

			<div className="bg-white rounded-xl shadow-sm border border-gray-100 h-[calc(100vh-230px)] p-0 overflow-hidden">
				{isLoading ? (
					<div className="flex items-center justify-center h-full">
						<div className="text-gray-500">Loading...</div>
					</div>
				) : (
					<VariantTable
						data={filterData}
						columns={processedColumns}
						customRowData={customRowData}
						sortValueInfo={sortInfo}
						virtualization
					/>
				)}
			</div>
		</div>
	);
}

export default IssuanceListPage;